// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { onboardingDe, moreDe } from "./onboarding.de";
import { viewsDe } from "./views.de";
// Bootstrapped from docs/product/main.html's `deCopy` EN→DE map (nav
// labels, "Anmelden"/"Registrieren", "Menü öffnen", "Zum Inhalt springen")
// where it already covers the shell; new keys are written fresh in the
// same informal "du" voice used throughout main.html.
export const de = {
    common: {
        appName: "BiteBalance",
        loading: "Lädt…",
        retry: "Erneut versuchen",
        close: "Schließen",
        dismiss: "Ausblenden",
        cancel: "Abbrechen",
        done: "Fertig",
        comingSoon: "Demnächst verfügbar",
        errorGeneric: "Etwas ist schiefgelaufen.",
        back: "Zurück",
    },
    nav: {
        tab: { today: "Heute", plan: "Plan", shop: "Einkauf", kitchen: "Küche", more: "Mehr" },
        today: "Heute",
        plan: "Plan",
        pantry: "Küche",
        shop: "Einkaufen",
        insights: "Einblicke",
        more: "Einstellungen",
        amy: "Amy",
        guides: "Guides",
        signOut: "Abmelden",
        openMenu: "Menü öffnen",
        skipToContent: "Zum Inhalt springen",
        primaryNavigation: "Hauptnavigation",
    },
    language: {
        label: "Sprache",
        de: "DE",
        en: "EN",
        switchHint: "Wechselt zu Englisch",
    },
    banner: {
        softLaunch: "BiteBalance ist neu. Funktionen und Inhalte können sich noch ändern — wir freuen uns über dein Feedback.",
        dismiss: "Hinweis ausblenden",
    },
    shell: {
        deploy: {
            newVersion: "Eine neue Version von BiteBalance ist verfügbar.",
            reload: "Neu laden",
            dismiss: "Später",
        },
    },
    landing: {
        tagline: "Iss passend zu deinen Zielen. Gib weniger aus. Wirf weniger weg.",
        logIn: "Anmelden",
        signUp: "Registrieren",
        freeAccessProof: "Kostenloser Zugang für 14 Tage · kein Abo",
    },
    auth: {
        logInTitle: "Anmelden",
        signUpTitle: "Konto erstellen",
        emailLabel: "E-Mail",
        passwordLabel: "Passwort",
        passwordHint: "Verwende mindestens 8 Zeichen.",
        confirmPasswordLabel: "Passwort bestätigen",
        agreeSignUpPrefix: "Ich stimme den",
        agreeSocialPrefix: "Wenn du fortfährst, stimmst du unseren",
        termsLink: "Nutzungsbedingungen",
        agreeConnector: "und der",
        privacyLink: "Datenschutzerklärung",
        agreeSuffix: " zu.",
        showPassword: "Passwort anzeigen",
        hidePassword: "Passwort verbergen",
        aiTransparency: "BiteBalance nutzt eine KI-Assistentin, Amy, um deine Essensplanung zu unterstützen. Amy schlägt Änderungen immer nur vor — nichts wird ohne deine Bestätigung übernommen.",
        createAccount: "Konto erstellen",
        freeClaimPerk: "🎁 14 Tage kostenloser Zugang · kein Abo, keine Zahlungsdaten",
        alreadyHaveAccount: "Schon ein Konto?",
        needAccount: "Noch kein Konto?",
        verifyTitle: "Bestätige deine E-Mail",
        verifyIntro: "Wir haben einen Bestätigungscode an {{email}} gesendet. Gib ihn unten ein, um dein Konto zu bestätigen.",
        verifyCodeLabel: "Bestätigungscode",
        verifyButton: "Konto bestätigen",
        resendCode: "Code erneut senden",
        resendCodeSent: "Wir haben einen neuen Code gesendet.",
        signInButton: "Anmelden",
        forgotPasswordLink: "Passwort vergessen?",
        forgotTitle: "Passwort zurücksetzen",
        forgotIntro: "Gib deine E-Mail ein, wir senden dir einen Code zum Zurücksetzen.",
        sendResetCode: "Code senden",
        resetTitle: "Neues Passwort festlegen",
        resetIntro: "Gib den Code ein, den wir an {{email}} gesendet haben, und wähle ein neues Passwort.",
        resetCodeLabel: "Code",
        newPasswordLabel: "Neues Passwort",
        resetButton: "Neues Passwort speichern",
        resetSuccess: "Passwort aktualisiert. Du kannst dich jetzt anmelden.",
        backToSignIn: "Zurück zur Anmeldung",
        signOutConfirm: "Du wurdest abgemeldet.",
        socialNeedsBuild: "Google-Anmeldung kommt mit der installierten App — nutze vorerst E-Mail.",
        continueWithGoogle: "Mit Google fortfahren",
        continueWithApple: "Mit Apple fortfahren",
        orSeparator: "oder",
        socialCompleting: "Anmeldung wird abgeschlossen…",
        errors: {
            UsernameExistsException: "Für diese E-Mail existiert bereits ein Konto.",
            InvalidPasswordException: "Dieses Passwort erfüllt nicht die Sicherheitsanforderungen.",
            InvalidParameterException: "Bitte überprüfe deine Eingaben.",
            CodeMismatchException: "Dieser Code ist nicht korrekt. Bitte versuche es erneut.",
            ExpiredCodeException: "Dieser Code ist abgelaufen. Fordere einen neuen an.",
            UserNotConfirmedException: "Dieses Konto ist noch nicht bestätigt. Prüfe deine E-Mails auf einen Code.",
            UserNotFoundException: "Wir konnten kein Konto mit dieser E-Mail finden.",
            NotAuthorizedException: "E-Mail oder Passwort ist nicht korrekt.",
            LimitExceededException: "Zu viele Versuche. Bitte warte einen Moment und versuche es erneut.",
            TooManyRequestsException: "Zu viele Versuche. Bitte warte einen Moment und versuche es erneut.",
            TooManyFailedAttemptsException: "Zu viele Versuche. Bitte warte einen Moment und versuche es erneut.",
            ChallengeRequired: "Dieses Konto benötigt einen zusätzlichen Bestätigungsschritt, den wir noch nicht unterstützen.",
            MissingRefreshToken: "Deine Sitzung konnte nicht gestartet werden. Bitte melde dich erneut an.",
            OAuthCancelled: "Die Anmeldung wurde abgebrochen.",
            OAuthStateMismatch: "Wir konnten diesen Anmeldeversuch nicht verifizieren. Bitte versuche es erneut.",
            OAuthTokenError: "Die Anmeldung über diesen Anbieter wurde nicht abgeschlossen. Bitte versuche es erneut.",
            generic: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
            consentRequired: "Bitte stimme der Datenschutzerklärung und den Nutzungsbedingungen zu, um fortzufahren.",
            passwordMismatch: "Die Passwörter stimmen nicht überein.",
        },
    },
    state: {
        loading: "Lädt…",
        retry: "Erneut versuchen",
        estimated: "Geschätzt",
        staleData: "Diese Daten könnten veraltet sein",
    },
    today: {
        title: "Heute",
        emptyTitle: "Dein Tag ist noch nicht geplant",
        emptyBody: "Sobald Onboarding und dein erster Plan fertig sind, erscheinen deine heutigen Mahlzeiten hier.",
        signedInAs: "Angemeldet als {{email}}",
        onboardingNotStarted: "Das Onboarding wurde noch nicht gestartet.",
        onboardingInProgress: "Das Onboarding läuft gerade.",
        onboardingCompleted: "Das Onboarding ist abgeschlossen.",
    },
    plan: {
        title: "Plan",
        emptyTitle: "Noch keine Woche geplant",
        emptyBody: "Dein Wochenplan, Vorkochen und gesperrte Mahlzeiten erscheinen hier, sobald er erstellt ist.",
    },
    shop: {
        title: "Einkaufen",
        emptyTitle: "Noch nichts zu kaufen",
        emptyBody: "Deine Vorräte und Einkaufsliste erscheinen hier, sobald du einen Plan hast.",
    },
    insights: {
        title: "Einblicke",
        moreLinkSub: "Ersparnisse, Konsequenz und dein Gewichtstrend",
        emptyTitle: "Noch keine Einblicke",
        emptyBody: "Trends zu Plantreue, Ersparnis und gesparter Zeit erscheinen hier, sobald Verlauf vorliegt.",
        learned: {
            title: "Was BiteBalance gelernt hat",
            caveat: "Nur, was wir aus deinen Einträgen belegen können.",
            mealsLogged: "geplante Mahlzeiten erfasst.",
            streak: "Tage in Folge mit erfasster Mahlzeit.",
            streakOne: "Tag mit erfasster Mahlzeit.",
            mealsUnlogged: "{{count}} noch ohne Ergebnis.",
            consistency: "deines Plans bisher befolgt.",
            realized: "realisierte Ersparnis aus bestätigten Ergebnissen.",
            projected: "geplante Ersparnis aus dieser Wochenplanung.",
            notShownTitle: "Nicht gezeigt",
            notShownBody: "Energie, Stimmung, Schlaf, Körperzusammensetzung, ein \u201eGesundheits-Score\u201c. BiteBalance misst das nicht und berichtet es deshalb nicht.",
        },
        progress: {
            chartTitle: "Deine Wiegungen",
            chartSinceStart: "seit {{date}}",
            chartEntriesLegend: "Wiegungen",
            chartTrendLegend: "7-Tage-Trend",
            sectionTitle: "Dein Fortschritt",
            weightLogTitle: "Gewicht eintragen",
            weightInputLabel: "Gewicht (kg)",
            weightDateLabel: "Datum",
            logButton: "Gewicht eintragen",
            loggedConfirmation: "Eingetragen",
            weightErrorInvalid: "Gib ein gültiges Gewicht ein",
            trendTitle: "7-Tage-Durchschnitt",
            scaleGuideLink: "Die Waage bewegt sich kaum? Lies, warum die Tageszahl täuscht",
            trendEmpty: "Trage eine zweite Messung ein, um einen Trend zu sehen — eine einzelne Messung reicht allein nie aus.",
            trendDirectionDown: "Im gezeigten Zeitraum tendenziell abnehmend",
            trendDirectionFlat: "Im gezeigten Zeitraum weitgehend stabil",
            trendDirectionUp: "Im gezeigten Zeitraum tendenziell zunehmend",
            trendDirectionPending: "Eine Richtung wird angezeigt, sobald mindestens zwei Wochen Daten vorliegen.",
            consistencyTitle: "Plantreue",
            consistencyBody: "Du hast bisher {{percent}}% deines Plans befolgt.",
            unavailableBody: "Fortschrittsdaten sind gerade nicht verfügbar — deine Einträge sind sicher, versuch es gleich noch mal.",
            consistencyEmpty: "Noch nichts geplant — die Plantreue erscheint, sobald du einen Plan hast.",
            calibratingTitle: "Die erste Woche ist Kalibrierung",
            calibratingBody: "Deine erste Woche ist Kalibrierung — logge weiter Mahlzeiten und Gewicht. Was du unten siehst, ist einfach das, was passiert ist; noch keine Bewertungen.",
            observingTitle: "Dein Trend baut sich auf",
            observingBody: "Trage weiter ein — mit mehr Daten wird das Bild klarer. Weiterhin nur Beobachtung, keine Bewertung.",
            reviewableTitle: "Du bist jetzt seit ein paar Wochen dabei",
            reviewableBody: "Du hast genug Verlauf für einen echten gemeinsamen Blick, wann immer du magst.",
            reviewCta: "Meinen Fortschritt mit Amy besprechen",
            reviewPrompt: "Kannst du dir meinen Fortschritt mit mir ansehen? Schau dir meine Plantreue und meinen Gewichtstrend an und hilf mir zu überlegen, was gut läuft und was ich vielleicht anpassen sollte.",
        },
    },
    more: {
        title: "Einstellungen",
        emptyTitle: "Kontoeinstellungen folgen bald",
        emptyBody: "Profil, Ziele, Vorlieben, Erinnerungen, Abrechnung und Datenschutz werden hier verwaltet.",
    },
    launch: {
        meErrorTitle: "Konto konnte nicht geladen werden",
        meErrorBody: "Wir konnten BiteBalance nicht erreichen, um deinen Kontostatus zu prüfen. Bitte Verbindung prüfen und erneut versuchen.",
    },
    biometrics: {
        offerTitle: "Mit {{method}} entsperren?",
        offerBody: "Schütze deine Ernährungsdaten auf diesem Gerät: {{method}} (oder dein Gerätecode) wird beim Öffnen der App abgefragt. Änderbar jederzeit unter Mehr.",
        enable: "{{method}} verwenden",
        notNow: "Später",
        unlockTitle: "BiteBalance ist gesperrt",
        unlockPrompt: "BiteBalance entsperren",
        unlockRetry: "Erneut versuchen",
        usePasscode: "Geräte-Code verwenden",
        faceIdBlockedHint: "{{method}} erscheint nicht? Prüfe in der iOS-App Einstellungen, ob {{method}} eingerichtet und für BiteBalance erlaubt ist. Dein Geräte-Code funktioniert solange.",
        moreToggle: "App-Sperre ({{method}})",
        moreToggleSub: "{{method}} oder Gerätecode beim Öffnen der App verlangen",
    },
    privacy: {
        title: "Datenschutz & Daten",
        moreLink: "Datenschutz & Daten",
        moreLinkSub: "Daten herunterladen oder Konto löschen",
        exportTitle: "Deine Daten herunterladen",
        exportBody: "Erhalte eine vollständige Kopie von allem, was BiteBalance über dich speichert — Profil, Pläne, Vorräte, Feedback, Erinnerungen und Chatverlauf — als eine JSON-Datei.",
        exportButton: "Export erstellen",
        exportWorking: "Export wird vorbereitet…",
        exportDownload: "Export herunterladen",
        exportExpiry: "Der Download-Link ist 15 Minuten gültig. Du kannst jederzeit einen neuen Export erstellen.",
        exportFailed: "Der Export konnte nicht erstellt werden. Bitte versuche es erneut.",
        deleteTitle: "Konto löschen",
        deleteBody: "Damit werden dein Konto und alle persönlichen Daten — Profil, Pläne, Vorräte, Erinnerungen, Chatverlauf und Login — dauerhaft aus allen unseren Systemen gelöscht. Das kann nicht rückgängig gemacht werden. Gesetzlich vorgeschriebene Abrechnungsnachweise bleiben ohne Bezug zu dir erhalten.",
        reauthLabel: "Bestätige dein Passwort, um fortzufahren",
        reauthButton: "Bestätigen",
        wrongPassword: "Das Passwort war nicht korrekt. Bitte versuche es erneut.",
        reauthExpired: "Deine Bestätigung ist abgelaufen — bitte gib dein Passwort erneut ein.",
        reconfirmLabel: "Bestätige zu deiner Sicherheit mit {{provider}}, dass du es bist",
        reconfirmButton: "Mit {{provider}} fortfahren",
        reconfirmWorking: "Wird bestätigt …",
        reconfirmUsePassword: "Ich habe ein Passwort für dieses Konto — stattdessen Passwort verwenden",
        reconfirmOtherAccount: "Das ist nicht das Konto, mit dem du angemeldet bist. Bitte bestätige mit demselben Konto.",
        reconfirmFailed: "Wir konnten deine Anmeldung nicht bestätigen. Bitte versuche es erneut.",
        reconfirmExpired: "Deine Bestätigung ist abgelaufen — bitte bestätige erneut, dass du es bist.",
        deleteConfirmPhrase: "LÖSCHEN",
        deleteConfirmLabel: "Tippe {{phrase}}, um die endgültige Löschung zu bestätigen",
        deleteButton: "Mein Konto endgültig löschen",
        deleteWorking: "Wird gelöscht…",
        deleteFailed: "Die Löschung konnte nicht abgeschlossen werden. Dein Konto ist weiterhin aktiv. Bitte versuche es erneut.",
        deletedTitle: "Kontolöschung gestartet",
        deletedBody: "Du wurdest abgemeldet und deine Daten werden nun dauerhaft gelöscht. Danke, dass du BiteBalance ausprobiert hast.",
        // ADR-085 D7 (Apple subscription is not cancelled by account deletion).
        appleSubscriptionWarning: "Wenn du dein Konto löschst, wird dein Apple-Abo nicht gekündigt. Kündige es in den Einstellungen deines Apple Accounts, sonst bucht Apple weiter ab.",
        appleSubscriptionManageLink: "Apple-Abo-Einstellungen öffnen",
        consentWithdrawLead: "Oder per E-Mail widerrufen:",
        consentWithdrawLink: "Schreib uns an privacy@bitebalance.de.",
        healthConsentTitle: "Einwilligung in Gesundheitsangaben",
        healthConsentActive: "Du hast eingewilligt, dass BiteBalance deine Gesundheitsangaben für deinen Plan und für Amy verarbeitet.",
        healthConsentWithdraw: "Einwilligung widerrufen",
        healthConsentWithdrawConfirmBody: "Wenn du widerrufst, löschen wir deine Gesundheitsangaben (Erkrankungen, Medikamente, Schwangerschaft oder Stillzeit, Allergien und Unverträglichkeiten, Gewicht und Größe) und erstellen keine Pläne und keine Amy-Antworten mehr für dich. Bestehende Pläne, Einkaufslisten und dein Chatverlauf bleiben gespeichert, bis du dein Konto löschst. Du kannst jederzeit erneut einwilligen und deine Angaben neu eingeben.",
        healthConsentWithdrawConfirm: "Ja, widerrufen und Angaben löschen",
        healthConsentKeep: "Abbrechen",
        healthConsentWithdrawing: "Widerruf läuft…",
        healthConsentWithdrawError: "Der Widerruf hat nicht geklappt. Prüfe deine Verbindung und versuche es erneut.",
        healthConsentWithdrawn: "Du hast widerrufen. Wir haben deine Gesundheitsangaben gelöscht und verarbeiten keine neuen.",
        healthConsentNone: "Du hast derzeit nicht eingewilligt.",
        analyticsLabel: "Produktanalyse (PostHog)",
        analyticsHelp: "Hilft uns zu verstehen, welche Schritte genutzt werden. Nur mit deiner Zustimmung.",
        adsLabel: "Marketing (Meta Pixel)",
        adsHelp: "Misst, ob unsere Anzeigen bei Meta (Facebook, Instagram) zu Anmeldungen führen. Nur mit deiner Zustimmung.",
        adsLabelApp: "Werbe-Kennungen für Meta",
        adsHelpApp: "Erlaubt Meta, Kennungen deines Geräts mit unseren Anzeigen abzugleichen. Die anonyme Werbemessung läuft auch ohne. Beim Einschalten fragt iOS einmal um die Tracking-Erlaubnis; ohne sie bleibt der Schalter aus.",
        adsTrackingDeniedApp: "iOS erlaubt BiteBalance derzeit kein Tracking. Ändern kannst du das unter Einstellungen → Datenschutz & Sicherheit → Tracking.",
        openIosSettings: "iOS-Einstellungen öffnen",
    },
    access: {
        title: "Dein Zugang",
        subtitle: "Wähle, wie du BiteBalance nutzen möchtest. Während der Pre-Launch-Phase ist dein erster Monat gratis — ohne Karte.",
        pilotNote: "Pilot-Zugang — kostenlos. Du gehörst zu unseren eingeladenen Testern.",
        activeTitle: "Dein Plan ist aktiv",
        freePlanTitle: "Gratis-Plan (Beta)",
        freePlanBody: "Voller Zugriff auf Pläne, Amy, Vorräte, Einkaufslisten und Insights während der Beta. Keine Zahlungsdaten nötig.",
        activateFree: "Gratis-Plan aktivieren",
        prelaunchTitle: "Gratis Pre-Launch-Monat",
        prelaunchBody: "Voller Zugriff auf Pläne, Amy, Vorräte, Einkaufslisten und Insights für einen Monat. Keine Karte, keine Verlängerung — der Zugang endet einfach nach 30 Tagen.",
        activatePrelaunch: "Gratis-Monat sichern",
        freeClaimTitle: "2 Wochen gratis",
        freeClaimBody: "Voller Zugriff auf Pläne, Amy, Vorräte, Einkaufslisten und Insights für 2 Wochen. Keine Karte, keine Verlängerung — der Zugang endet einfach nach 14 Tagen.",
        activateFreeClaim: "2 Wochen gratis sichern",
        freeClaimActivating: "Dein kostenloser Zugang wird eingerichtet…",
        freeClaimReady: "Dein 14-tägiger kostenloser Zugang ist bereit. Keine Kreditkarte nötig.",
        paidIntro: "Oder wähle jetzt deinen Plan",
        trialBadge: "14 Tage gratis",
        trialNote: "danach {{price}} · jederzeit vor Ende der Testphase kündbar",
        choosePlan: "Gratis testen",
        subscribeNow: "Jetzt abonnieren",
        returnConfirming: "Willkommen zurück! Wir bestätigen deinen Plan gerade mit dem Zahlungsanbieter — diese Seite aktualisiert sich automatisch.",
        manageTitle: "Kündigung",
        manageBody: "Jederzeit kündbar — dein Zugang bleibt immer bis zum Ende des bereits bezahlten Zeitraums (oder deines Gratis-Monats) bestehen. Nach der Kündigung wird nichts mehr abgebucht.",
        freePrice: "Gratis",
        badgeEnding: "endet",
        perMonth: "/Monat",
        perQuarter: " / 3 Monate",
        perYear: "/Jahr",
        subPrelaunchLine: "🎁 Dein gratis Pre-Launch-Monat läuft bis zum {{date}}. Nichts verlängert sich, keine Karte ist hinterlegt — wähle unten jederzeit einen Plan, um danach weiterzumachen.",
        subPrelaunchLineNoDate: "🎁 Dein gratis Pre-Launch-Monat ist aktiv. Nichts verlängert sich, keine Karte ist hinterlegt — wähle unten jederzeit einen Plan, um danach weiterzumachen.",
        subPrelaunchLineUrgent: "⏰ Dein gratis Pre-Launch-Monat endet am {{date}} — danach verlierst du den Zugang, wenn du nicht unten einen Plan wählst.",
        upgradeFromPrelaunchIntro: "So geht's nach deinem Gratis-Monat weiter",
        subFreeClaimLine: "🎁 Deine 2 gratis Wochen laufen bis zum {{date}}. Nichts verlängert sich, keine Karte ist hinterlegt — wähle unten jederzeit einen Plan, um danach weiterzumachen.",
        subFreeClaimLineNoDate: "🎁 Deine 2 gratis Wochen sind aktiv. Nichts verlängert sich, keine Karte ist hinterlegt — wähle unten jederzeit einen Plan, um danach weiterzumachen.",
        subFreeClaimLineUrgent: "⏰ Deine 2 gratis Wochen enden am {{date}} — danach verlierst du den Zugang, wenn du nicht unten einen Plan wählst.",
        upgradeFromFreeClaimIntro: "So geht's nach deinen 2 gratis Wochen weiter",
        switchPlanHint: "Du möchtest einen anderen Plan? Kündige zuerst den aktuellen — du behältst vollen Zugang bis zum Ende des bezahlten Zeitraums — und wähle dann hier jeden beliebigen Plan.",
        subTrialLine: "🎁 Kostenlose Testphase — endet am {{date}}. Direkt danach startet dein Abo und es werden {{price}} abgebucht. Kündige jederzeit vorher und du zahlst nichts.",
        subRenewLine: "Dein Abo verlängert sich automatisch am {{date}} für {{price}}.",
        subCancelledLine: "Gekündigt — dein Zugang bleibt bis zum {{date}} bestehen. Es wird nichts mehr abgebucht.",
        paymentTitle: "Zahlung",
        paymentBody: "Deine Zahlungsmethode wird sicher bei Whop, unserem Zahlungsanbieter, gespeichert — wir sehen deine Kartendaten nie. Sollte eine Abbuchung fehlschlagen, schickt dir Whop per E-Mail einen sicheren Link zum Aktualisieren deiner Karte. Für alles andere erreichst du uns unter",
        invoicesTitle: "Rechnungen & Belege",
        invoicesBody: "Whop schickt für jede Abbuchung automatisch einen Beleg an deine Checkout-E-Mail — nichts zu suchen, er landet direkt im Postfach.",
        cancelCta: "Abo kündigen",
        cancelConfirmBody: "Plan wirklich kündigen? Du behältst vollen Zugang bis zum Ende des aktuellen Zeitraums, und es wird nichts mehr abgebucht. Du kannst jederzeit einen neuen Plan starten.",
        cancelConfirmCta: "Ja, Plan kündigen",
        cancelKeep: "Plan behalten",
        cancelDone: "Gekündigt. Dein Zugang bleibt bis zum {{date}} bestehen — es wird nichts mehr abgebucht.",
        cancelDoneNoDate: "Gekündigt. Dein Zugang bleibt bis zum Ende des aktuellen Zeitraums bestehen — es wird nichts mehr abgebucht.",
        cancelError: "Die Kündigung hat gerade nicht geklappt. Versuch es gleich noch einmal — oder melde dich bei uns, wir kümmern uns darum.",
        waitingForConfirmation: "Warten auf Bestätigung…",
        checkoutHint: "Schließe die Aktivierung im geöffneten Checkout-Tab ab. Diese Seite aktualisiert sich automatisch, sobald dein Zugang bestätigt ist.",
        pollTimedOut: "Wir haben die Bestätigung noch nicht erhalten. Wenn du den Checkout abgeschlossen hast, warte kurz und öffne diese Seite erneut — Zugang wird nur von unserem Server vergeben, nie vom Browser.",
        noOffer: "Aktuell ist kein Plan zur Aktivierung verfügbar. Du kannst die App weiter nutzen.",
        comingSoonTitle: "Kommt später",
        comingSoon: "Bald verfügbar",
        continue: "Weiter zu Heute",
        loadError: "Dein Zugangsstatus konnte nicht geladen werden.",
        checkoutRefused: {
            appleActive: "Du hast bereits ein BiteBalance-Abo über Apple. Verwalte es in den Einstellungen deines Apple Accounts.",
            freeUsed: "Dein kostenloser Zugang wurde für dieses Konto bereits genutzt.",
            retired: "Abos gibt es jetzt in der BiteBalance-App für iPhone.",
        },
        detailsToggle: "Plan- und Abrechnungsdetails",
        reducedActiveEyebrow: "🎁 Dein Zugang ist aktiv",
        reducedFreeUntil: "Gratis bis {{date}}.",
        reducedFreeUntilNoDate: "Dein gratis Zugang ist aktiv.",
        reducedNoCharge: "Keine Karte hinterlegt. Heute keine Kosten.",
        moreLink: "Plan & Abrechnung",
        moreLinkSub: "Aktuellen Zugang ansehen und Plan aktivieren",
        // ADR-085 IAP-3: Apple In-App Purchase paywall (mobile only).
        apple: {
            rowUnavailable: "Im App Store gerade nicht verfügbar",
            planUnavailableA11y: "{{plan}}. Im App Store gerade nicht verfügbar.",
            plansPartialNote: "Einige Pläne sind im App Store gerade nicht verfügbar. Sobald sie bereitstehen, erscheinen sie hier.",
            plansNoneNote: "Der App Store hat gerade keine Pläne geliefert. Bitte versuche es gleich noch einmal.",
            renewsLine: "Verlängert sich automatisch zum Preis von {{price}}, bis du kündigst.",
            planA11y: "{{plan}}, {{price}}, verlängert sich automatisch, bis du kündigst. Abonnieren",
            disclosure: "Der Betrag wird bei Bestätigung des Kaufs über deinen Apple Account abgebucht. Dein Abo verlängert sich automatisch, sofern du nicht mindestens 24 Stunden vor Ende des laufenden Zeitraums kündigst. Die Abbuchung für die Verlängerung erfolgt innerhalb von 24 Stunden vor Ende des laufenden Zeitraums. Du kannst dein Abo jederzeit in den Einstellungen deines Apple Accounts verwalten oder kündigen.",
            termsLink: "Nutzungsbedingungen",
            privacyLink: "Datenschutzerklärung",
            restoreCta: "Käufe wiederherstellen",
            restoring: "Deine Käufe werden geprüft…",
            restoreNothing: "Für diesen Apple Account haben wir kein aktives Abo gefunden.",
            restoreDone: "Dein Abo ist auf diesem Gerät wieder aktiv.",
            purchasing: "Warten auf Apple…",
            verifying: "Dein Kauf wird bestätigt…",
            applied: "Dein Plan ist aktiv. Danke!",
            cancelledNote: "Kein Problem – es wurde nichts abgebucht.",
            pendingApproval: "Dein Kauf wartet auf Freigabe. Sobald er bestätigt ist, aktivieren wir deinen Plan – du musst nichts weiter tun.",
            interruptedNote: "Apple braucht noch einen Schritt. Sobald du ihn abgeschlossen hast, wird dein Plan automatisch aktiviert.",
            alreadyOwnedNote: "Dieser Apple Account hat bereits ein BiteBalance-Abo. Tippe auf „Käufe wiederherstellen“, um es hier zu nutzen.",
            noResultNote: "Gerade wurde nichts bestätigt. Falls du den Kauf abgeschlossen hast, wird dein Plan automatisch aktiviert, sobald Apple ihn bestätigt.",
            genericNote: "Beim App Store ist etwas schiefgelaufen. Bitte versuche es später noch einmal.",
            networkNote: "Der App Store war nicht erreichbar. Prüfe deine Verbindung und versuche es noch einmal.",
            storeUnavailable: "In-App-Käufe sind auf diesem Gerät gerade nicht möglich. Versuche es später noch einmal.",
            unsupportedDevice: "Abos gibt es in der BiteBalance-App für iPhone.",
            verifyFailedRetry: "Dein Kauf ist sicher, aber wir konnten ihn gerade nicht bestätigen. Bitte versuche es noch einmal.",
            retryCta: "Noch einmal versuchen",
            verifyFailedContact: "Wir konnten diesen Kauf nicht mit Apple bestätigen. Bitte schreib uns an {{email}}, wir kümmern uns darum.",
            notGrantedSandbox: "Wir haben deinen Kauf erhalten, aber er kann für dieses Konto gerade nicht aktiviert werden. Falls dir etwas abgebucht wurde, schreib uns an {{email}}.",
            notActive: "Dieses Abo ist beendet. Wähle einen Plan, um weiterzumachen.",
            otherAccount: "Das Abo dieses Apple Accounts gehört zu einem anderen BiteBalance-Konto. Melde dich mit diesem Konto an, um es zu nutzen, oder abonniere hier mit einem anderen Apple Account.",
            featureDisabled: "Abonnieren in der App ist gerade nicht möglich. Bitte versuche es später noch einmal.",
            freeUsedLine: "Dein kostenloser Zugang wurde für dieses Konto bereits genutzt.",
            blockedWhopTitle: "Du hast bereits eine Mitgliedschaft",
            blockedWhopBody: "Deine aktuelle Mitgliedschaft läuft bis {{date}}. Danach kannst du hier einen Plan wählen.",
            blockedWhopBodyNoDate: "Deine aktuelle Mitgliedschaft ist noch aktiv. Danach kannst du hier einen Plan wählen.",
            paymentProblemGrace: "Mit deiner Zahlung gibt es ein Problem. Du behältst den Zugang bis {{date}} – bitte aktualisiere deine Zahlungsdaten in deinem Apple Account.",
            paymentProblemPaused: "Deine Zahlung wird erneut versucht, der Zugang ist pausiert. Aktualisiere deine Zahlungsdaten in deinem Apple Account oder wähle unten einen Plan.",
            manageTitle: "Abo verwalten",
            manageBody: "Ändere oder kündige deinen Plan in den Einstellungen deines Apple Accounts. Nach einer Kündigung behältst du den Zugang bis zum Ende des bezahlten Zeitraums.",
            manageCta: "Apple-Abo-Einstellungen öffnen",
            paymentTitle: "Zahlung & Belege",
            paymentBody: "Apple übernimmt die Zahlung und schickt dir für jede Abbuchung einen Beleg per E-Mail. Deine Kartendaten sehen wir nie. Fragen? Schreib uns an",
            renewLine: "Verlängert sich am {{date}} zum Preis von {{price}}.",
            renewLineNoPrice: "Verlängert sich am {{date}}.",
            // App Review 2026-10-30 (3.1.2(c)): der kostenlose Zugang ist von den Abos GETRENNT — nie eine Testphase, nie ein Plan.
            freeAccessName: "Kostenloser Zugang",
            freeAccessLine: "Kostenloser Zugang bis {{date}}. Das ist kein Abo: keine Zahlungsdaten, keine Verlängerung, keine Kosten.",
            freeAccessLineNoDate: "Dein kostenloser Zugang ist aktiv. Das ist kein Abo: keine Zahlungsdaten, keine Verlängerung, keine Kosten.",
            freeAccessLineUrgent: "Dein kostenloser Zugang endet am {{date}}. Wenn du BiteBalance danach weiter nutzen möchtest, kannst du ein Abo abschließen.",
            subscriptionsHeading: "Abos",
            subscriptionsNote: "Die Abos haben keinen kostenlosen Testzeitraum: Apple berechnet den Preis, sobald ein Abo beginnt, und es verlängert sich automatisch, bis du kündigst.",
            freeAccessEyebrow: "Dein Zugang ist aktiv",
            freeAccessUntil: "Kostenloser Zugang bis {{date}}.",
            freeAccessUntilNoDate: "Dein kostenloser Zugang ist aktiv.",
            freeAccessSummaryNote: "Kein Abo. Keine Zahlungsdaten, keine Kosten.",
            freeAccessOfferTitle: "14 Tage kostenloser Zugang",
            freeAccessOfferBody: "Nutze alle Funktionen 14 Tage lang kostenlos. Das ist unabhängig von den Abos: keine Zahlungsdaten, keine Verlängerung, nichts zu kündigen.",
            freeAccessOfferCta: "Kostenlosen Zugang starten",
            freeAccessActivating: "Kostenloser Zugang wird eingerichtet …",
            freeAccessReady: "Dein kostenloser Zugang ist bereit: 14 Tage, kein Abo, keine Zahlungsdaten.",
            accessActive: "Dein Zugang ist aktiv",
        },
        web: {
            subtitle: "Dein Zugang zu BiteBalance und wie es weitergeht.",
            appStoreOnlyTitle: "Abonnieren in der BiteBalance-App für iPhone",
            appStoreOnlyBody: "Pläne und Preise siehst du in der App, bezahlt wird über deinen Apple Account. Alles, was du hier im Web machst, bleibt mit der App synchron.",
            noIphoneNote: "Kein iPhone? Dein kostenloser Zugang funktioniert auch hier im Web; ein Abo gibt es derzeit nur über die iPhone-App.",
            freeStartTitle: "14 Tage kostenloser Zugang",
            freeStartBody: "Voller Zugang für 14 Tage. Das ist kein Abo: keine Zahlungsdaten, keine Verlängerung, nichts zu kündigen. Ein Abo kannst du danach in der BiteBalance-App für iPhone abschließen.",
            freeStartCta: "Kostenlosen Zugang starten",
            freeStartFailed: "Wir konnten deinen kostenlosen Zugang gerade nicht starten. Bitte versuche es gleich noch einmal.",
            continueInAppLine: "Um nach dem {{date}} weiterzumachen, wähle in der BiteBalance-App für iPhone einen Plan.",
            continueInAppLineNoDate: "Um weiterzumachen, wähle in der BiteBalance-App für iPhone einen Plan.",
            appleRenewsOn: "Verlängert sich am {{date}}. Preis und Zahlung laufen über Apple.",
            appleManagedLine: "Dieses Abo wird in deinem Apple Account verwaltet.",
            paymentProblemPaused: "Deine Zahlung wird erneut versucht, der Zugang ist pausiert. Aktualisiere deine Zahlungsdaten in deinem Apple Account.",
        },
        planName: {
            free: "Gratis-Plan",
            prelaunch: "Gratis Pre-Launch-Monat",
            freeClaim: "2-Wochen-Gratisplan",
            monthly: "Monatlich",
            quarterly: "3 Monate",
            annual: "Jährlich",
        },
        source: {
            pilot: "Pilot-Zugang",
            individual: "Einzelplan",
            b2b: "Team-Zugang",
            none: "Kein Plan",
        },
        state: {
            active: "aktiv",
            trialing: "Testphase",
            past_due: "Zahlung fällig",
            canceled: "gekündigt",
            expired: "abgelaufen",
            none: "nicht aktiv",
        },
    },
    consent: {
        body: "Wir möchten Cookies verwenden, um zu verstehen, wie BiteBalance gefunden und genutzt wird, damit wir es verbessern können. Wir aktivieren dies nur mit deiner Zustimmung.",
        accept: "Akzeptieren",
        decline: "Ablehnen",
        webBody: "Wir möchten zwei optionale Dienste nutzen: PostHog, um zu verstehen, wie BiteBalance genutzt wird, und den Meta Pixel, um zu messen, ob unsere Anzeigen bei Facebook und Instagram zu Anmeldungen führen. Beides nur mit deiner Zustimmung.",
        analyticsOption: "Produktanalyse (PostHog)",
        adsOption: "Marketing (Meta Pixel)",
        appBody: "Wir möchten PostHog nutzen, um zu verstehen, wie BiteBalance genutzt wird – nur mit deiner Zustimmung. Zur Werbemessung nutzen wir das Meta SDK ohne Werbe-Kennung; ob Meta zusätzlich Kennungen deines Geräts verwenden darf, entscheidest du hier. Wenn du Werbe-Kennungen erlaubst, fragt iOS dich anschließend einmal um die Tracking-Erlaubnis.",
        adsOptionApp: "Werbe-Kennungen für Meta",
        save: "Auswahl speichern",
        acceptAll: "Alle akzeptieren",
        settingsLink: "Cookie-Einstellungen",
    },
    healthConsent: {
        eyebrow: "Deine Einwilligung",
        title: "Darf BiteBalance mit deinen Gesundheitsangaben arbeiten?",
        body: "Für deinen Plan verarbeiten wir deine Gesundheitsdaten im Sinne von Art. 9 DSGVO: Erkrankungen, Medikamente, Schwangerschaft oder Stillzeit, Allergien und Unverträglichkeiten sowie Gewicht und Größe. Unsere KI Amy nutzt dafür Claude von Anthropic über Amazon Bedrock in der EU. Auch deine Nachrichten und Fotos an Amy gehen dorthin. Anthropic erhält keinen Zugriff auf deine Daten; Amazon Web Services verarbeitet sie in unserem Auftrag in der EU und kann Ein- und Ausgaben zur Missbrauchserkennung befristet speichern.",
        checkbox: "Ich willige ausdrücklich ein, dass BiteBalance meine Gesundheitsangaben dafür verarbeitet (Art. 9 Abs. 2 lit. a DSGVO).",
        footnote: "Du kannst die Einwilligung jederzeit widerrufen, in der App unter Mehr → Datenschutz & Daten, per E-Mail an privacy@bitebalance.de oder indem du dein Konto löschst. Dann löschen wir deine Gesundheitsangaben. Ohne Einwilligung können wir keinen Plan für dich erstellen.",
        privacyLink: "Datenschutzerklärung",
        continue: "Weiter",
        saving: "Speichert…",
        saveError: "Das hat nicht geklappt. Prüfe deine Verbindung und versuche es erneut.",
        declineHint: "Nicht einverstanden? Du kannst deine Daten herunterladen oder dein Konto löschen.",
        backToConsent: "Zurück zur Einwilligung",
    },
    amy: {
        panel: {
            expand: "Amy vergrößern",
            collapse: "Amy verkleinern",
            close: "Amy schließen",
            widen: "Amy verbreitern",
            narrow: "Amy schmaler machen",
        },
        editMessage: "Nachricht bearbeiten",
        editingNotice: "Du bearbeitest eine frühere Nachricht. Die neue Version wird am Ende angehängt.",
        editCancel: "Bearbeiten abbrechen",
        attachPhoto: "Foto",
        attachFile: "Datei",
        attachTitle: "Zur Nachricht hinzufügen",
        fileTooLarge: "Diese Datei ist zu groß (max. ~3 MB).",
        fileUnsupported: "Unterstützte Dateien: PDF, CSV, TXT, MD, DOCX, XLSX.",
        fileChipRemove: "Datei entfernen",
        micDevBuildHint: "Spracheingabe kommt mit der installierten App — nutze solange die Mikrofon-Taste deiner Tastatur.",
        suggestionsLabel: "Frag zum Beispiel",
        copyMessage: "Nachricht kopieren",
        copiedMessage: "Kopiert",
        title: "Amy",
        askAmy: "Amy fragen",
        askAmyHint: "Öffnet Amy, die KI-Assistentin von BiteBalance.",
        openButton: "Amy öffnen",
        closeButton: "Amy schließen",
        inputLabel: "Nachricht an Amy",
        inputPlaceholder: "z.\u202fB. Mach diese Woche g\u00fcnstiger",
        send: "Senden",
        thinking: "Amy denkt nach\u2026",
        toolRunning: "Gleicht mit deinen Plandaten ab\u2026",
        error: "Amy konnte gerade nicht antworten. Bitte versuche es erneut.",
        // ADR-058 Entscheidung 5 — statt eines erneuten Sendens, sobald Text
        // oder ein Werkzeug bereits zurückkam: ein Retry könnte Amys Arbeit doppeln.
        interrupted: "Amys Antwort wurde unterbrochen. Falls sie gerade etwas geändert hat, prüfe Heute und Plan, bevor du erneut fragst.",
        turn: {
            stillWorking: "Amy arbeitet noch daran \u2013 du kannst die App verlassen und sp\u00e4ter zur\u00fcckkommen.",
            answeredWhileAway: "Beantwortet, w\u00e4hrend du weg warst",
            couldNotFinish: "Amy konnte das nicht abschlie\u00dfen. Falls sie etwas ge\u00e4ndert hat, pr\u00fcfe Heute und Plan, bevor du erneut fragst.",
            notReceived: "Deine Nachricht ist nicht bei Amy angekommen. Bitte sende sie noch einmal.",
            answeredTitle: "Amy hat geantwortet",
            answeredBody: "Deine Antwort ist in BiteBalance bereit.",
        },
        disclaimer: "Amy schl\u00e4gt vor \u2014 ohne deine ausdr\u00fcckliche Zustimmung \u00e4ndert sich nichts, und jede \u00c4nderung durchl\u00e4uft dieselben Sicherheitspr\u00fcfungen wie der Rest der App.",
        emptyHint: "Frag Amy zu deiner Woche \u2014 Tausch, g\u00fcnstigere Optionen oder was in einem Rezept steckt.",
        greeting: {
            hiName: "Hallo {{name}}!",
            hiGeneric: "Hallo!",
            subtitle: "Je mehr du teilst, desto besser passt dein Plan.",
            promptsLabel: "Frag zum Beispiel",
            promptAteOut: "Ich war mittags essen \u2014 passt mein Tag trotzdem noch?",
            promptCheaper: "Mach diese Woche g\u00fcnstiger",
            promptRecipe: "Was steckt im heutigen Abendessen?",
        },
        historyLoading: "Dein letztes Gespr\u00e4ch wird geladen\u2026",
        historyUnavailable: "Amys Ged\u00e4chtnis war gerade nicht erreichbar \u2014 du kannst trotzdem chatten, fr\u00fchere Nachrichten werden aber m\u00f6glicherweise nicht angezeigt.",
        // ADR-068 Fresh Adaptive Amy (mobile): geerdeter Einstieg + Composer-Texte.
        headerSub: "Kennt Plan, K\u00fcche und deine Grenzen",
        sees: {
            title: "Was Amy gerade sieht",
            eatenToday: "Heute gegessen",
            nextUp: "Als N\u00e4chstes",
            week: "Diese Woche",
            kitchen: "K\u00fcche",
            allLogged: "Alles erfasst",
            meal: "1 Mahlzeit",
            meals: "{{n}} Mahlzeiten",
            expired: "{{name}} ist abgelaufen",
            expiresToday: "{{name}} l\u00e4uft heute ab",
            expiresTomorrow: "{{name}} l\u00e4uft morgen ab",
            nothingExpiring: "Nichts l\u00e4uft ab",
        },
        composerPlaceholder: "Frag Amy alles zu dieser Woche",
        composerSend: "Fragen",
        sendWhileBusy: "Amy antwortet — du kannst weiter tippen und senden, sobald sie fertig ist.",
        // F4 \u2014 Spracheingabe (Web-SpeechRecognition; mobiles Mikrofon folgt mit dem Dev-Build, D-7)
        voice: {
            start: "Nachricht diktieren",
            stop: "Diktat beenden",
            listening: "Ich h\u00f6re zu\u2026 tippe erneut auf das Mikrofon, um zu stoppen.",
            permissionDenied: "BiteBalance hat keinen Mikrofonzugriff. Du kannst ihn in den Website-Einstellungen deines Browsers erlauben \u2014 oder einfach weiter tippen.",
            error: "Die Spracheingabe hat gerade nicht funktioniert. Bitte versuche es erneut oder tippe weiter.",
        },
        // F5 \u2014 Foto-Anh\u00e4nge (Mahlzeit-, K\u00fchlschrank-, Kassenbon- oder Etikettenfotos)
        photo: {
            attach: "Foto anh\u00e4ngen",
            remove: "Foto entfernen",
            previewAlt: "Foto bereit zum Senden",
            bubbleAlt: "Dein gesendetes Foto",
            placeholder: "[Foto]",
            defaultPrompt: "Was kannst du mir zu diesem Foto sagen?",
            tooLarge: "Dieses Foto ist auch nach der Komprimierung zu gro\u00df. Bitte w\u00e4hle ein kleineres.",
            readError: "Dieses Foto konnte nicht gelesen werden. Bitte versuche ein anderes.",
            sourceTitle: "Foto hinzuf\u00fcgen",
            takePhoto: "Foto aufnehmen",
            chooseFromLibrary: "Aus der Mediathek w\u00e4hlen",
            cameraPermissionDenied: "BiteBalance hat keinen Kamerazugriff. Du kannst ihn in den Ger\u00e4teeinstellungen erlauben oder stattdessen ein Foto aus deiner Mediathek w\u00e4hlen.",
        },
    },
    onboarding: onboardingDe,
    moreProfile: moreDe,
    amyCompanion: {
        morningTitle: "Starte den Tag mit Amy",
        morningSubtitle: "Ein kurzes Wort mit Amy — und der Tag sitzt.",
        morningChip1: "Worauf sollte ich heute achten?",
        morningChip2: "Können wir heute etwas günstiger machen?",
        dayTitle: "Wie läuft dein Tag?",
        daySubtitle: "Pläne ändern sich — sag es Amy und sie balanciert den Rest.",
        dayChip1: "Ich habe mittags etwas anderes gegessen",
        dayChip2: "Was ist heute Abend geplant?",
        eveningTitle: "Abend-Check-in",
        eveningSubtitle: "Zwei Tipps und morgen passt schon.",
        eveningChip1: "Ich habe heute außerplanmäßig gegessen — hilf mir auszugleichen",
        eveningChip2: "Wie war mein Tag?",
        snapChip: "Fotografiere dein Essen",
    },
    checkin: {
        toggleLabel: "Täglicher Abend-Check-in",
        toggleBody: "Eine freundliche Erinnerung von Amy gegen 20 Uhr, ihr zu erzählen, wie der Tag lief. Wird nur auf diesem Gerät gespeichert.",
        permissionDenied: "Mitteilungen sind für BiteBalance in den Geräteeinstellungen deaktiviert.",
        notifTitle: "Abend-Check-in 🌙",
        notifBody: "Wie lief dein Tag? Zwei Tipps und morgen passt schon.",
        mealTitle: "Wie war {{meal}}?",
        mealBody: "Eintragen oder Amy sagen, was anders lief — dein Tag bleibt aktuell.",
    },
    prehook: {
        eyebrow: "FÜR DICH GEBAUT",
        eyebrowNamed: "FÜR {{name}} GEBAUT",
        title: "Deine Woche gibt es schon.",
        readyTitle: "Deine Woche ist fertig.",
        intro: "Wir haben sie gebaut, während du fertig wurdest — {{days}} Tage, {{meals}} Mahlzeiten, Zeile für Zeile an deinen Angaben geprüft.",
        scoreOf: "/100",
        fitSeal: "Passform-Check",
        saidGoal: "Du sagtest: {{goal}}",
        receiptGoal: "Tagesziel {{kcal}} — innerhalb sicherer, begrenzter Grenzen.",
        saidTime: "Du sagtest: ca. {{limit}} Min. zum Kochen",
        receiptTimeOk: "Deine Hauptmahlzeiten brauchen im Schnitt {{avg}} Min. aktiv.",
        receiptTimeOver: "Im Schnitt sind es gerade {{avg}} Min. — Amy kann die langsamen tauschen.",
        saidNoTime: "Du hast kein Zeitlimit gesetzt",
        receiptNoTime: "Jedes Rezept zeigt trotzdem seine Minuten.",
        saidBudget: "Du sagtest: ca. {{budget}} pro Woche",
        receiptBudgetOk: "Deine Woche kostet {{cost}} — {{delta}} darunter.",
        receiptBudgetOver: "Deine Woche kostet {{cost}} — {{delta}} darüber. Tauschen kann das schließen.",
        saidNoBudget: "Du hast kein Budget gesetzt",
        receiptNoBudget: "Kosten stehen trotzdem an jeder Portion.",
        saidFood: "Du sagtest: {{style}}",
        receiptFood: "0 Konflikte in {{meals}} Mahlzeiten · {{distinct}} verschiedene Gerichte.",
        receiptFoodPending: "Diese Woche wird noch an deine Regeln angepasst.",
        teaseHeading: "Als Erstes aus deiner Woche",
        teaseSlot: "{{day}} · {{slot}}",
        teaseMore: "…und {{count}} weitere Mahlzeiten warten.",
        firstUpSuffix: "als Erstes",
        fitPill: "{{score}}/100 Fit",
        countsPill: "{{meals}} Mahlzeiten · {{days}} Tage",
        disclosureLabel: "So haben wir zu deinen Angaben gepasst",
        cta: "Meine Woche öffnen",
        ctaNote: "Dein 14-tägiger kostenloser Zugang ist enthalten — kein Abo, keine Zahlungsdaten.",
        footnote: "Jede Zeile oben stammt aus deinem echten Plan.",
    },
    planSync: {
        updatingTitle: "Dein Plan wird aktualisiert",
        targetChangedLine: "Dein tägliches Kalorienziel ist jetzt {{to}} (vorher {{from}}).",
        updatingBody: "Wir bauen deinen Plan für heute und die kommende Woche neu, damit er zu deinen Einstellungen passt. Das dauert einen Moment.",
        doneTitle: "Plan aktualisiert",
        doneBody: "Dein Plan für heute und die kommende Woche passt jetzt zu deinen Einstellungen.",
        doneBodyWithTarget: "Dein Plan für heute und die kommende Woche passt jetzt zu deinem Tagesziel von {{to}}.",
        errorTitle: "Dein Plan konnte nicht aktualisiert werden",
        errorBody: "Deine neuen Einstellungen sind gespeichert, aber der Neuaufbau des Plans ist fehlgeschlagen. Dein aktueller Plan ist unverändert — versuche es erneut.",
        retryCta: "Erneut versuchen",
    },
    views: viewsDe,
};
